<template>
  <div>
    <v-dialog
      v-model="internalVisible"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      persistent
    >
      <v-card class="detail-card d-flex flex-column" style="height:100vh;">

        <!-- Toolbar -->
        <div class="dlg-toolbar flex-shrink-0 d-flex align-center px-3">
          <v-btn icon dark @click="close">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
          <v-avatar color="accent" size="36" class="ml-2 mr-3">
            <v-icon small color="white">fas fa-file-invoice</v-icon>
          </v-avatar>
          <div>
            <div class="subtitle-1 font-weight-black white--text lh-1">Detalle DTE — Relbase</div>
            <div v-if="doc" class="caption accent--text lh-1 mt-1">
              Folio #{{ doc.folio }} · {{ doc.doc_type_display }}
            </div>
          </div>
          <v-spacer />
          <v-btn
            v-if="pdfUrl"
            outlined
            color="accent"
            class="font-weight-black mr-2"
            :href="pdfUrl"
            target="_blank"
            rel="noopener"
          >
            <v-icon left small>fas fa-file-pdf</v-icon>
            Ver PDF
          </v-btn>
        </div>

        <!-- Body -->
        <div class="detail-body flex-grow-1 overflow-y-auto pa-6">

          <!-- Loading -->
          <div v-if="loading" class="d-flex flex-column align-center justify-center" style="min-height:300px;">
            <v-progress-circular indeterminate color="primary" size="52" />
            <div class="caption grey--text mt-5">Cargando datos desde Relbase...</div>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="d-flex flex-column align-center justify-center" style="min-height:300px;">
            <v-icon color="error" size="56" class="mb-4">fas fa-exclamation-circle</v-icon>
            <div class="body-1 font-weight-bold error--text mb-2">Error al cargar el documento</div>
            <div class="caption grey--text">{{ error }}</div>
          </div>

          <!-- Content -->
          <div v-else-if="relbaseData">
            <v-row>
              <!-- Columna izquierda: datos del documento -->
              <v-col cols="12" md="8">

                <!-- Encabezado del documento -->
                <v-card outlined class="modern-card mb-5">
                  <div class="doc-header d-flex justify-space-between align-start pa-6">
                    <div>
                      <div class="text-h6 font-weight-black primary--text">{{ companyName }}</div>
                      <div class="caption grey--text mt-1">{{ companyGiro }}</div>
                      <div class="caption grey--text">RUT: {{ companyRut }}</div>
                    </div>
                    <div class="text-right">
                      <div class="doc-badge">
                        <div class="doc-badge-type">{{ relbaseData.type_document_name }}</div>
                        <div class="doc-badge-folio">N° {{ relbaseData.folio }}</div>
                        <div class="mt-2">
                          <v-chip
                            v-if="relbaseData.sii_status_name"
                            small
                            :color="siiStatusColor"
                            class="font-weight-black"
                          >
                            <v-icon left x-small>fas fa-landmark</v-icon>
                            {{ relbaseData.sii_status_name }}
                          </v-chip>
                          <v-chip v-else small color="grey lighten-3" class="font-weight-bold grey--text">
                            <v-icon left x-small>fas fa-clock</v-icon>
                            Pendiente SII
                          </v-chip>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card>

                <!-- Tabla de productos -->
                <v-card outlined class="modern-card mb-5">
                  <div class="px-6 pt-5 pb-2">
                    <div class="section-title mb-4">
                      <v-icon x-small color="accent" class="mr-2">fas fa-list</v-icon>
                      LÍNEAS DE DETALLE
                    </div>
                    <table class="detail-table">
                      <thead>
                        <tr>
                          <th class="text-left">Código</th>
                          <th class="text-left">Descripción</th>
                          <th class="text-center">Cant.</th>
                          <th class="text-right">P. Unit. Neto</th>
                          <th class="text-right">Total Neto</th>
                          <th class="text-center">Afecto</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(p, i) in relbaseData.products"
                          :key="i"
                          :class="i % 2 === 0 ? 'row-even' : 'row-odd'"
                        >
                          <td class="td-sku">{{ p.code || '—' }}</td>
                          <td class="td-name">{{ p.description || p.name }}</td>
                          <td class="text-center td-qty">{{ p.real_quantity }}</td>
                          <td class="text-right td-price">{{ unitNeto(p) | moneda }}</td>
                          <td class="text-right td-total font-weight-bold">{{ p.real_amount_neto | moneda }}</td>
                          <td class="text-center">
                            <v-chip
                              x-small
                              :color="p.tax_affected ? 'accent lighten-4' : 'grey lighten-3'"
                              class="font-weight-bold"
                            >{{ p.tax_affected ? 'AFT' : 'EXN' }}</v-chip>
                          </td>
                        </tr>
                        <tr v-if="!relbaseData.products || relbaseData.products.length === 0">
                          <td colspan="6" class="text-center grey--text caption py-6">Sin líneas de detalle</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Totales -->
                  <div class="d-flex justify-end pa-6 pt-3">
                    <div class="totals-box">
                      <div class="totals-row">
                        <span class="totals-label">Neto Afecto</span>
                        <span class="totals-amount">{{ relbaseData.amount_neto | moneda }}</span>
                      </div>
                      <div v-if="relbaseData.amount_exempt > 0" class="totals-row">
                        <span class="totals-label">Exento</span>
                        <span class="totals-amount">{{ relbaseData.amount_exempt | moneda }}</span>
                      </div>
                      <div class="totals-row">
                        <span class="totals-label">IVA ({{ relbaseData.iva }}%)</span>
                        <span class="totals-amount">{{ relbaseData.amount_iva | moneda }}</span>
                      </div>
                      <v-divider class="my-2" />
                      <div class="totals-row totals-grand">
                        <span class="totals-label-grand">TOTAL</span>
                        <span class="totals-amount-grand">{{ relbaseData.amount_total | moneda }}</span>
                      </div>
                    </div>
                  </div>
                </v-card>

              </v-col>

              <!-- Columna derecha: metadatos -->
              <v-col cols="12" md="4">

                <!-- Receptor -->
                <v-card outlined class="modern-card mb-5 pa-5">
                  <div class="section-title mb-4">
                    <v-icon x-small color="accent" class="mr-2">fas fa-user-tie</v-icon>
                    RECEPTOR
                  </div>
                  <div class="meta-label">Cliente</div>
                  <div class="meta-value">{{ doc ? doc.customer_name : '—' }}</div>
                  <div class="meta-label mt-3">Dirección</div>
                  <div class="meta-value">{{ relbaseData.address || '—' }}</div>
                  <div class="meta-label mt-3">Comuna / Ciudad</div>
                  <div class="meta-value">{{ relbaseData.str_commune }} · {{ relbaseData.str_city }}</div>
                </v-card>

                <!-- Datos de emisión -->
                <v-card outlined class="modern-card mb-5 pa-5">
                  <div class="section-title mb-4">
                    <v-icon x-small color="accent" class="mr-2">fas fa-calendar-alt</v-icon>
                    DATOS DE EMISIÓN
                  </div>
                  <div class="meta-label">Fecha Emisión</div>
                  <div class="meta-value">{{ relbaseData.start_date | moment('DD/MM/YYYY') }}</div>
                  <div class="meta-label mt-3">IVA</div>
                  <div class="meta-value">{{ relbaseData.iva }}%</div>
                  <div class="meta-label mt-3">Estado SII</div>
                  <div class="meta-value">
                    <v-chip
                      v-if="relbaseData.sii_status_name"
                      small
                      :color="siiStatusColor"
                      class="font-weight-black"
                    >{{ relbaseData.sii_status_name }}</v-chip>
                    <span v-else class="caption grey--text">Pendiente de envío al SII</span>
                  </div>
                  <div class="meta-label mt-3">Glosa / Referencia</div>
                  <div class="meta-value caption">{{ relbaseData.comment || '—' }}</div>
                </v-card>

                <!-- PDF -->
                <v-card v-if="pdfUrl" outlined class="modern-card pa-5">
                  <div class="section-title mb-4">
                    <v-icon x-small color="accent" class="mr-2">fas fa-file-pdf</v-icon>
                    DOCUMENTO PDF
                  </div>
                  <v-btn
                    block
                    color="accent"
                    class="font-weight-black"
                    :href="pdfUrl"
                    target="_blank"
                    rel="noopener"
                  >
                    <v-icon left small>fas fa-download</v-icon>
                    Descargar / Ver PDF
                  </v-btn>
                </v-card>

                <!-- Footer Relbase ID -->
                <div class="mt-4 caption grey--text text-center">
                  <v-icon x-small class="mr-1 accent--text">fas fa-check-circle</v-icon>
                  Datos desde <strong>Relbase</strong> · ID {{ relbaseData.id }}
                </div>

              </v-col>
            </v-row>
          </div>

        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { fetchRelbaseDTEDetail } from '@/api/relbase'
import notifications from '@/utils/notifications'

export default {
  name: 'RelbaseDteDetailDialog',

  props: {
    value: { type: Boolean, default: false },
    doc: { type: Object, default: null },
  },

  data() {
    return {
      loading: false,
      error: null,
      relbaseData: null,
    }
  },

  computed: {
    internalVisible: {
      get() { return this.value },
      set(val) { this.$emit('input', val) },
    },
    companyName() {
      return this.$store.getters.getCompany?.name || ''
    },
    companyGiro() {
      return this.$store.getters.getCompany?.giro || ''
    },
    companyRut() {
      return this.$store.getters.getCompany?.rut || ''
    },
    pdfUrl() {
      return this.relbaseData?.pdf_file?.url || null
    },
    siiStatusColor() {
      const name = (this.relbaseData?.sii_status_name || '').toLowerCase()
      if (name.includes('acept')) return 'success'
      if (name.includes('rechaz') || name.includes('anula')) return 'error'
      return 'grey'
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.load()
      } else {
        this.relbaseData = null
        this.error = null
      }
    },
  },

  methods: {
    async load() {
      if (!this.doc) return
      this.loading = true
      this.error = null
      this.relbaseData = null
      try {
        const res = await fetchRelbaseDTEDetail(this.doc.id)
        this.relbaseData = res.data || res
      } catch (e) {
        const msg = e?.response?.data?.error || e?.response?.data?.message || 'Error de conexión con Relbase'
        this.error = msg
        notifications.showError(msg)
      } finally {
        this.loading = false
      }
    },
    unitNeto(product) {
      const qty = parseFloat(product.real_quantity) || 1
      return Math.round(product.real_amount_neto / qty)
    },
    close() {
      this.internalVisible = false
    },
  },
}
</script>

<style scoped>
.dlg-toolbar { height: 64px; min-height: 64px; max-height: 64px; background-color: #1A1F2E; }
.detail-card { background-color: #f4f7f9; }
.detail-body { background-color: #f4f7f9; }

.modern-card {
  border-radius: 10px !important;
  border: 1px solid rgba(0,0,0,0.06) !important;
  background: white !important;
}

.doc-header { background: linear-gradient(135deg, #f8fafc 0%, #eef6f7 100%); border-radius: 10px; }

.doc-badge {
  background: #1A1F2E;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  text-align: center;
  min-width: 180px;
}
.doc-badge-type {
  font-size: 0.65rem;
  font-weight: 900;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  opacity: 0.8;
}
.doc-badge-folio {
  font-size: 1.3rem;
  font-weight: 900;
  color: #35BDC8;
  margin-top: 4px;
}

.section-title {
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 2px;
  color: #35BDC8;
  text-transform: uppercase;
  display: flex;
  align-items: center;
}

.meta-label {
  font-size: 0.68rem;
  color: #90a4ae;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.meta-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1A1F2E;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.83rem;
  margin-bottom: 4px;
}
.detail-table thead tr { background: #f0f4f7; }
.detail-table th {
  padding: 8px 10px;
  font-size: 0.68rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #546e7a;
  border-bottom: 2px solid #e0e7ef;
}
.detail-table td { padding: 9px 10px; border-bottom: 1px solid #f0f4f7; vertical-align: middle; }
.row-even { background: white; }
.row-odd  { background: #fafbfc; }
.td-sku   { font-weight: 700; color: #35BDC8; font-size: 0.78rem; }
.td-name  { color: #1A1F2E; }
.td-qty   { color: #607d8b; }
.td-price { color: #607d8b; font-variant-numeric: tabular-nums; }
.td-total { color: #1A1F2E; font-variant-numeric: tabular-nums; }

.totals-box { min-width: 260px; }
.totals-row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; }
.totals-label { font-size: 0.82rem; color: #607d8b; font-weight: 600; }
.totals-amount { font-size: 0.9rem; font-weight: 700; color: #1A1F2E; font-variant-numeric: tabular-nums; }
.totals-grand { padding-top: 6px; }
.totals-label-grand { font-size: 0.9rem; font-weight: 900; color: #1A1F2E; letter-spacing: 1px; text-transform: uppercase; }
.totals-amount-grand { font-size: 1.5rem; font-weight: 900; color: #1A1F2E; font-variant-numeric: tabular-nums; letter-spacing: -0.5px; }

.lh-1 { line-height: 1 !important; }
</style>
